import React, { useState, useEffect } from "react";
import "./Workload.css";
import { ChevronLeft, ChevronRight, Search, ChevronDown } from "lucide-react";
import CalendarDateViewModal from "../Tickets/modal/calendar-date-view.js";
import WorkloadDetailModal from "./WorkloadDetailModal";
import WorkloadMemberDetail from "./WorkloadMemberDetail";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchWorkloadData } from "./Workloadapi";
import { Spinner } from "react-bootstrap";
import ProfileIcon from "../../Assets/png/user-icon.png";


const Workload = () => {
  const [view, setView] = useState(sessionStorage.getItem('workload_view') || "Monthly");
  const [currentDate, setCurrentDate] = useState(moment()); // Current real-time date
  const [showDateModelView, setShowDateModelView] = useState(false);
  const [selectedOnDay, setSelectedOnDay] = useState(null);
  const [showWorkloadDetailModal, setShowWorkloadDetailModal] = useState(false);
  const [selectedMemberDetail, setSelectedMemberDetail] = useState(null);
  const [selectedWorkloadDate, setSelectedWorkloadDate] = useState(null);
  const [selectedEmployeeForPage, setSelectedEmployeeForPage] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const isFutureNavDisabled = () => {
    const nextDate = moment(currentDate).add(1, (view === 'Monthly' || view === 'Daily') ? 'month' : 'week');
    if (view === 'Monthly' || view === 'Daily') {
      return nextDate.startOf('month').isAfter(moment().startOf('month'));
    } else {
      return nextDate.startOf('isoWeek').isAfter(moment().startOf('isoWeek'));
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchWorkload = async (currentSkip, isReset = false) => {
    setLoading(true);
    try {
      const periodFormat = (view === "Monthly" || view === "Daily") ? "YYYY-MM" : "YYYY-MM-DD";
      const period = currentDate.format(periodFormat);

      const response = await fetchWorkloadData(view, period, debouncedSearchTerm, 10, currentSkip);

      if (response && response.data && response.data.success) {
        const rawData = response.data.data;
        const newMembers = rawData.members || [];
        if (newMembers.length < 10) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
        setEmployees(prev => {
          if (isReset || currentSkip === 0) {
            return newMembers;
          } else {
            const existingIds = new Set(prev.map(emp => emp.id));
            const filteredNew = newMembers.filter(emp => !existingIds.has(emp.id));
            return [...prev, ...filteredNew];
          }
        });
      }
    } catch (error) {
      console.error("Failed to fetch workload data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSkip(0);
    setHasMore(true);
    fetchWorkload(0, true);
  }, [view, currentDate, debouncedSearchTerm]);

  useEffect(() => {
    if (skip > 0) {
      fetchWorkload(skip, false);
    }
  }, [skip]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loading) {
      setSkip(prev => prev + 10);
    }
  };

  // Helper to get start of the range based on view
  const getStartOfRange = () => {
    if (view === "Weekly") return moment(currentDate).startOf('isoWeek'); // Starts Monday
    return moment(currentDate).startOf('month');
  };

  const rangeStart = getStartOfRange();

  // Configuration based on active view
  const config = {
    Daily: { total: currentDate.daysInMonth(), label: "Day" },
    Monthly: { total: currentDate.daysInMonth(), label: "Day" },
    Weekly: { total: 7, label: "Day" }
  };

  const currentConfig = config[view] || config.Monthly;
  const markers = Array.from({ length: currentConfig.total }, (_, i) => i + 1);

  const getGridStyle = () => {
    const colWidth = (view === "Monthly" || view === "Daily") ? "45px" : "1fr";
    return {
      display: "grid",
      gridTemplateColumns: `260px repeat(${currentConfig.total}, ${colWidth})`,
      width: "max-content",
      minWidth: "100%"
    };
  };

  if (selectedEmployeeForPage) {
    return <WorkloadMemberDetail
      member={selectedEmployeeForPage}
      onBack={() => {
        setSelectedEmployeeForPage(null);
        fetchWorkload(0, true);
      }}
      onSuccess={() => fetchWorkload(0, true)}
    />;
  }

  return (
    <div className="workload-page">
      <div className="workload-header-row">
        <div className="view-switcher">
          {["Monthly", "Weekly", "Daily"].map(v => (
            <button
              key={v}
              className={`view-btn ${view === v ? "active" : ""}`}
              onClick={() => {
                sessionStorage.setItem('workload_view', v);
                setView(v);
              }}
            >
              {v}
            </button>
          ))}
        </div>

        <div className="date-nav">
          <button className="nav-arrow" onClick={() => setCurrentDate(prev => moment(prev).subtract(1, (view === 'Monthly' || view === 'Daily') ? 'month' : 'week'))}>
            <ChevronLeft size={20} />
          </button>
          <span className="date-text">
            <DatePicker
              selected={currentDate.toDate()}
              onChange={(date) => setCurrentDate(moment(date))}
              maxDate={new Date()}
              customInput={
                <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {(view === "Monthly" || view === "Daily")
                    ? currentDate.format("MMMM YYYY")
                    : currentDate.format("MMMM DD, YYYY")}
                  <ChevronDown size={16} />
                </span>
              }
              dateFormat={(view === "Monthly" || view === "Daily") ? "MMMM yyyy" : "MMMM dd, yyyy"}
              showMonthYearPicker={view === "Monthly" || view === "Daily"}
            />
          </span>
          <button
            className="nav-arrow"
            disabled={isFutureNavDisabled()}
            onClick={() => {
              if (!isFutureNavDisabled()) {
                setCurrentDate(prev => moment(prev).add(1, (view === 'Monthly' || view === 'Daily') ? 'month' : 'week'));
              }
            }}
            style={{
              opacity: isFutureNavDisabled() ? 0.5 : 1,
              cursor: isFutureNavDisabled() ? "not-allowed" : "pointer"
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="search-container">
          <Search size={18} className="search-ic" />
          <input
            type="text"
            placeholder="Search member.."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="workload-grid-wrapper" onScroll={handleScroll}>
        <div className="workload-grid">
          {/* Header Row: Contextual Header (Only for Monthly) */}
          {view === "Monthly" && (
            <div className="grid-header-weeks" style={getGridStyle()}>
              <div className="member-header">
                <span>Members ({employees.length}) / {view}</span>
              </div>
              <div className="week-header-box week1">Week 1</div>
              <div className="week-header-box week2">Week 2</div>
              <div className="week-header-box week3">Week 3</div>
              <div
                className="week-header-box week4"
                style={{ borderRight: currentDate.daysInMonth() === 28 ? 'none' : '' }}
              >
                Week 4
              </div>
              {currentDate.daysInMonth() > 28 && (
                <div
                  className="week-header-box week5"
                  style={{ gridColumn: `30 / span ${currentDate.daysInMonth() - 28}` }}
                >
                  Week 5
                </div>
              )}
            </div>
          )}

          {/* Header Row 2: Markers (Days or Hours) */}
          <div className={`grid-header-days ${(view === "Weekly" || view === "Daily") ? "weekly-header" : ""}`} style={getGridStyle()}>
            <div className={view === "Monthly" ? "member-header-empty" : "member-header"}>
              <span>{(view === "Weekly" || view === "Daily") ? `Members (${employees.length}) / ${view}` : ""}</span>
            </div>
            {markers.map(m => {
              const today = moment();
              const isToday = view === "Weekly"
                ? moment(rangeStart).add(m - 1, 'days').isSame(today, 'day')
                : (currentDate.isSame(today, 'month') && currentDate.isSame(today, 'year') && m === today.date());

              return (
                <div
                  key={m}
                  className={`day-header-box ${((view === "Monthly" || view === "Daily") && m % 7 === 0) ? "week-end" : ""} ${isToday ? "highlight" : ""}`}
                >
                  <span>
                    {view === "Weekly"
                      ? moment(rangeStart).add(m - 1, 'days').format("ddd DD")
                      : m}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Body Rows */}
          <div className="grid-body">
            {employees.map(member => {
              let weeklyLabelData = null;
              if (view === "Weekly" && member.days) {
                const totalMin = Object.values(member.days).reduce((acc, d) => {
                  if (moment(d.date).isAfter(moment(), 'day')) {
                    return acc;
                  }
                  const p = (d.hours || "00:00").split(":").map(Number);
                  return acc + (p[0] * 60) + (p[1] || 0);
                }, 0);

                if (totalMin > 2400) {
                  const hoursStr = `${Math.floor(totalMin / 60).toString().padStart(2, '0')}:${(totalMin % 60).toString().padStart(2, '0')}`;
                  weeklyLabelData = { text: `${hoursStr} hr over`, color: "#E02424" };
                } else if (totalMin === 0) {
                  weeklyLabelData = { text: `40 hr free`, color: "#057A55" };
                } else {
                  const hoursStr = `${Math.floor(totalMin / 60).toString().padStart(2, '0')}:${(totalMin % 60).toString().padStart(2, '0')}`;
                  weeklyLabelData = { text: `${hoursStr} hr work`, color: "#057A55" };
                }
              }

              let dailyTotalLabel = null;
              if (view === "Daily" && member.days) {
                const totalMin = Object.values(member.days).reduce((acc, d) => {
                  if (moment(d.date).isAfter(moment(), 'day')) {
                    return acc;
                  }
                  const p = (d.hours || "00:00").split(":").map(Number);
                  return acc + (p[0] * 60) + (p[1] || 0);
                }, 0);
                const hours = Math.floor(totalMin / 60).toString().padStart(2, '0');
                const mins = (totalMin % 60).toString().padStart(2, '0');
                dailyTotalLabel = `${hours}:${mins} hr Work`;
              }

              return (
                <div key={member.id} className="grid-body-row" style={getGridStyle()}>
                  <div className="member-cell">
                    <div className="member-card" onClick={() => setSelectedEmployeeForPage(member)} style={{ cursor: "pointer" }}>
                      <img src={member.profile_image || ProfileIcon} alt={member.name} className="member-img" />
                      <div className="member-info">
                        <div className="name">{member.name}</div>
                        <div className="role">{member.designation}</div>
                      </div>
                      <ChevronRight size={18} className="row-arrow" />
                    </div>
                  </div>
                  <div className="timeline-cell" style={{ gridColumn: `2 / span ${currentConfig.total}` }}>
                    <div className="timeline-bg-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${currentConfig.total}, ${(view === "Monthly" || view === "Daily") ? "45px" : "1fr"})`,
                      width: "100%"
                    }}>
                      {markers.map(m => (
                        <div key={m} className={`grid-col ${((view === "Monthly" || view === "Daily") && m % 7 === 0) ? "week-end" : ""}`}></div>
                      ))}
                    </div>
                    <div className="timeline-overlay-bars">
                      {view === "Weekly" && weeklyLabelData && (
                        <div className="bar-label-text" style={{ bottom: "2px", color: weeklyLabelData.color }}>
                          {weeklyLabelData.text}
                        </div>
                      )}
                      {view === "Daily" && dailyTotalLabel && (
                        <div className="bar-label-text" style={{ bottom: "2px", left: "0px", color: "#057A55", paddingLeft: "4px" }}>
                          {dailyTotalLabel}
                        </div>
                      )}
                      {view === "Monthly" && member.days && [1, 8, 15, 22, 29].map(startDay => {
                        if (startDay > currentConfig.total) return null;
                        const endDay = startDay + 6;

                        let totalMin = 0;
                        let hasData = false;

                        Object.values(member.days).forEach(d => {
                          const dayOfMonth = moment(d.date).date();
                          if (dayOfMonth >= startDay && dayOfMonth <= endDay) {
                            if (moment(d.date).isAfter(moment(), 'day')) {
                              return;
                            }
                            hasData = true;
                            const p = (d.hours || "00:00").split(":").map(Number);
                            totalMin += (p[0] * 60) + (p[1] || 0);
                          }
                        });

                        if (!hasData) return null;

                        let monthWeekLabelData = null;
                        if (totalMin > 2400) {
                          const hoursStr = `${Math.floor(totalMin / 60).toString().padStart(2, '0')}:${(totalMin % 60).toString().padStart(2, '0')}`;
                          monthWeekLabelData = { text: `${hoursStr} hr over`, color: "#E02424" };
                        } else if (totalMin === 0) {
                          monthWeekLabelData = { text: `40 hr free`, color: "#057A55" };
                        } else {
                          const hoursStr = `${Math.floor(totalMin / 60).toString().padStart(2, '0')}:${(totalMin % 60).toString().padStart(2, '0')}`;
                          monthWeekLabelData = { text: `${hoursStr} hr work`, color: "#057A55" };
                        }

                        const leftPos = ((startDay - 1) / currentConfig.total) * 100;

                        return (
                          <div key={`week-tot-${startDay}`} className="bar-label-text" style={{ bottom: "2px", left: `${leftPos}%`, color: monthWeekLabelData.color, paddingLeft: "4px" }}>
                            {monthWeekLabelData.text}
                          </div>
                        );
                      })}
                      {(() => {
                        // If the API provides workload directly, use it
                        if (member.workload && member.workload.length > 0) {
                          return member.workload.map((item, idx) => ({
                            ...item,
                            label: ""
                          }));
                        }

                        // Otherwise, generate bars from the 'days' object
                        if (!member.days) return [];

                        const generatedWorkload = [];
                        const dayKeys = Object.keys(member.days).map(Number).sort((a, b) => a - b);

                        let currentBar = null;

                        const parseHours = (h) => {
                          if (!h) return 0;
                          const parts = h.split(':').map(Number);
                          if (parts.length < 2) return 0;
                          return parts[0] + (parts[1] / 60);
                        };

                        dayKeys.forEach((dayNum) => {
                          const dayData = member.days[dayNum];
                          const status = dayData.status; // 'red', 'green', or 'none'
                          const hrs = dayData.hours || '00:00:00';
                          const fullDate = dayData.date;

                          if (moment(fullDate).isAfter(moment(), 'day')) {
                            if (currentBar) generatedWorkload.push(currentBar);
                            currentBar = null;
                            return;
                          }

                          if (status === 'none') {
                            if (currentBar) generatedWorkload.push(currentBar);
                            currentBar = null;
                            return;
                          }

                          const type = status === 'red' ? 'over' : 'free';

                          // Group only if status AND hours are the same
                          if (currentBar && currentBar.type === type && currentBar.rawHours === hrs) {
                            currentBar.end = dayNum + 1;
                          } else {
                            if (currentBar) generatedWorkload.push(currentBar);
                            currentBar = {
                              start: dayNum,
                              end: dayNum + 1,
                              date: fullDate,
                              type: type,
                              rawHours: hrs,
                              hours: parseHours(hrs),
                              label: ""
                            };
                          }
                        });

                        if (currentBar) generatedWorkload.push(currentBar);

                        return generatedWorkload;
                      })().map((item, idx) => {
                        const itemDate = moment(item.date);
                        const relStart = (view === "Monthly" || view === "Daily") ? itemDate.date() : (itemDate.diff(rangeStart, 'days') + 1);
                        const relEnd = relStart + (item.end - item.start);

                        // Skip if completely out of view
                        if (relEnd <= 0 || relStart > currentConfig.total) return null;

                        const clampedStart = Math.max(1, relStart);
                        const clampedEnd = Math.min(currentConfig.total + 1, relEnd);

                        return (
                          <div
                            key={idx}
                            className={`work-bar ${item.type}`}
                            title={`${item.rawHours} - ${item.type === 'over' ? 'Over Capacity' : 'Free Capacity'}`}
                            style={{
                              left: `${((clampedStart - 1) / currentConfig.total) * 100}%`,
                              width: `${((clampedEnd - clampedStart) / currentConfig.total) * 100}%`,
                              height: `${Math.min(60, Math.max(10, (item.hours / 8) * 40))}px`,
                              cursor: (item.type === "free" || item.type === "over") ? "pointer" : "default"
                            }}
                            onClick={() => {
                              if (item.type === "free" || item.type === "over") {
                                setSelectedMemberDetail(member);
                                setSelectedWorkloadDate(item.date); // This is the 'fullDate' from the loop
                                setShowWorkloadDetailModal(true);
                              }
                            }}
                          >
                            {item.label && <span className="bar-label-text">{item.label}</span>}
                          </div>
                        );
                      })}

                    </div>
                  </div>
                </div>
              );
            })}

            {loading && employees.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', width: '100%', minWidth: '400px', gap: '12px', background: '#FFFFFF' }}>
                <Spinner animation="border" variant="primary" style={{ color: '#6929E2' }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#6929E2' }}>Loading workload data...</span>
              </div>
            )}

            {loading && employees.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', width: '100%', gap: '10px', background: '#FFFFFF' }}>
                <Spinner animation="border" size="sm" variant="primary" style={{ color: '#6929E2' }} />
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#6929E2' }}>Loading more Employees...</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {showDateModelView && (
        <CalendarDateViewModal
          selectedDate={selectedOnDay}
          listingType="assigned_me"
          onHide={() => setShowDateModelView(false)}
        />
      )}
      {showWorkloadDetailModal && selectedMemberDetail && (
        <WorkloadDetailModal
          member={selectedMemberDetail}
          date={selectedWorkloadDate}
          onClose={() => setShowWorkloadDetailModal(false)}
          onSuccess={() => fetchWorkload()}
        />
      )}
    </div>
  );
};

export default Workload;
